import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { Controller } from "react-hook-form";
import TextInput from "../../../components/inputs/TextInput";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { ActiveDirectoryConfigSchema } from "../../../schemas/activeDirectoryConfigSchema";
import { useCallback, useEffect, useState } from "react";
import ShowHidePasswordCard from "../../../components/card/ShowHidePasswordCard";
import { useGetActiveDirectoryConfig } from "../../../hooks/common/query";
import {
  useTestActiveDirectoryConnection,
  useUpdateActiveDirectoryConfig,
} from "../../../hooks/common/mutation";
import useAuthStore from "../../../store/Auth";
import toast, { Toaster } from "react-hot-toast";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";

const enum SUBMIT_TYPE {
  SAVE = "SAVE",
  TEST_CONNECTION = "TEST_CONNECTION",
}

const activeDirectoryConfigInitialState = {
  adUserGroupName: "",
  domain: "",
  ldapPath: "",
  password: "",
  username: "",
};

interface ActiveDirectoryConfigData {
  adUserGroupName?: string;
  domain: string;
  ldapPath: string;
  password: string;
  username: string;
}

const ActiveDirectoryConfig = () => {
  const { t } = useTranslation();

  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  // const [passwordValidation, setPasswordValidation] = useState(
  //   passwordValidationInitialState
  // );

  const {
    mutateAsync: UpdateActiveDirectoryConfig,
    isPending: isUpdateActiveDirectoryConfigLoading,
  } = useUpdateActiveDirectoryConfig();

  const {
    mutateAsync: TestActiveDirectoryConnection,
    isPending: isTestActiveDirectoryConnectionLoading,
  } = useTestActiveDirectoryConnection();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useFormWithSchema(ActiveDirectoryConfigSchema);

  const { data: activeDirectoryConfig } = useGetActiveDirectoryConfig();

  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (activeDirectoryConfig) {
      setValue("username", activeDirectoryConfig.activeDirectoryUsername);
      activeDirectoryConfig.activeDirectoryPassword &&
        setValue("password", activeDirectoryConfig.activeDirectoryPassword);
      setValue("domain", activeDirectoryConfig.activeDirectoryDomain);
      setValue("adUserGroupName", activeDirectoryConfig.adUserGroupName);
      setValue("ldapPath", activeDirectoryConfig.activeDirectoryLDAPPath);
    }
  }, [activeDirectoryConfig, setValue]);

  const onSubmit = useCallback(
    async (data: ActiveDirectoryConfigData, type: SUBMIT_TYPE) => {
      try {
        const body = {
          activeDirectoryUsername: data.username,
          activeDirectoryPassword: data.password,
          activeDirectoryDomain: data.domain,
          activeDirectoryLDAPPath: data.ldapPath,
          adUserGroupName: data.adUserGroupName,
        };

        if (type === SUBMIT_TYPE.TEST_CONNECTION) {
          const { isAuthenticated } = await TestActiveDirectoryConnection(body);

          if (isAuthenticated) {
            toast.success(t("connectionEstablished"));
          } else {
            toast.error(t("connectionError"));
          }
        } else {
          if (user?.user_id) {
            const updateBody = {
              ...body,
              n_UserID: user?.user_id,
            };
            await UpdateActiveDirectoryConfig(updateBody);
            toast.success(t("directoryUpdated"));
          }
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [
      TestActiveDirectoryConnection,
      UpdateActiveDirectoryConfig,
      user?.user_id,
      t,
    ]
  );

  // const validatePassword = useCallback((password: string) => {
  //   setPasswordValidation({
  //     length: password.length >= 8,
  //     uppercase: /[A-Z]/.test(password),
  //     lowercase: /[a-z]/.test(password),
  //     number: /\d/.test(password),
  //     specialChar: /[!@#$%^&*(),.?":{}|<>_+=-]/.test(password),
  //   });
  // }, []);

  const handleTogglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible((prev) => !prev);
  }, []);

  const handleClear = useCallback(() => {
    reset(activeDirectoryConfigInitialState);
    // setPasswordValidation(passwordValidationInitialState);
  }, [reset]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("activeDirectoryConfig")}
          </h1>
        </BackButtonWrapper>

        <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 lg:gap-4">
              <Controller
                name="username"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("username")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                    required
                  />
                )}
              />

              <div className="relative flex flex-col gap-1">
                <ShowHidePasswordCard
                  isPasswordVisible={isPasswordVisible}
                  handleTogglePasswordVisibility={
                    handleTogglePasswordVisibility
                  }
                  labelStyle="text-base"
                />
                <Controller
                  name="password"
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <TextInput
                      type={isPasswordVisible ? "text" : "password"}
                      label={t("password")}
                      value={value || ""}
                      onChange={(e) => {
                        onChange(e);
                        // validatePassword(e.target.value);
                      }}
                      errorStyle="-bottom-[15px]"
                      required
                      error={errors?.[name]?.message}
                      // isInfoToolTip
                      // infoToolTip={
                      //   <ul
                      //     className={cn(
                      //       "list-disc text-xs flex flex-col",
                      //       i18n.language === "en" ? "ml-3" : "mr-3"
                      //     )}
                      //   >
                      //     {passwordRequirements.map((requirement, index) => (
                      //       <li key={index}>{requirement}</li>
                      //     ))}
                      //   </ul>
                      // }
                    />
                  )}
                />

                {/* <PasswordSafetyCard passwordValidation={passwordValidation} /> */}
              </div>

              <Controller
                name="domain"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("domain")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                    required
                  />
                )}
              />

              <Controller
                name="ldapPath"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("ldapPath")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                    required
                  />
                )}
              />

              <Controller
                name="adUserGroupName"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("adUserGroupName")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                  />
                )}
              />
            </div>

            <div className="flex items-center gap-2 mt-2">
              <Button
                isLoading={isUpdateActiveDirectoryConfigLoading}
                onClick={handleSubmit((data) =>
                  onSubmit(data, SUBMIT_TYPE.SAVE)
                )}
              >
                {t("save")}
              </Button>
              <Button
                type="button"
                onClick={handleClear}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("clear")}
              </Button>

              <Button
                isLoading={isTestActiveDirectoryConnectionLoading}
                onClick={handleSubmit((data) =>
                  onSubmit(data, SUBMIT_TYPE.TEST_CONNECTION)
                )}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("testConnection")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ActiveDirectoryConfig;
